import {
  createSearchParamsCache,
  parseAsBoolean,
  parseAsInteger,
  parseAsJson,
} from 'nuqs/server';
import { z } from 'zod';
import { addDays } from './utils';

const coordinatesSchema = z.array(
  z.object({
    lat: z.number().optional(),
    lng: z.number().optional(),
    title: z.string().optional(),
    slugBooqable: z.string().optional(),
  }),
);

const filterParamsSchema = z.array(z.string());

const needsParamsSchema = z.object({
  voyagePartner: z.union([
    z.literal('family'),
    z.literal('friends'),
    z.literal('single'),
    z.literal('couple'),
  ]),
  physicalCondition: z.union([
    z.literal('active'),
    z.literal('regular'),
    z.literal('casual'),
    z.literal('unused'),
  ]),
  firstTime: z.boolean().optional(),
  bikeType: z.union([z.literal('VAE'), z.literal('VTC')]),
  budget: z.object({
    min: z.number().optional(),
    max: z.number().optional(),
  }),
  numberOfPeople: z.number().optional(),
});

const travelModeSchema = z.object({
  mode: z.union([
    z.literal('dolcevia'),
    z.literal('tourist'),
    z.literal('road'),
    z.literal('off-road'),
  ]),
});

const stepsSelectionSchema = z.array(
  z.object({
    index: z.number(),
    stepIndex: z.number(),
    habitation: z.array(
      z.object({
        lat: z.number(),
        lng: z.number(),
        name: z.string(),
        url: z.string(),
        type: z.union([
          z.literal('greengo'),
          z.literal('booking'),
          z.literal('airbnb'),
          z.literal('hotel'),
        ]),
        isAccueilVelo: z.boolean(),
        isPartenaire: z.boolean().optional(),
        price: z.number(),
        image: z.string(),
      }),
    ),
    interest: z.array(
      z.object({
        lat: z.number(),
        lng: z.number(),
        name: z.string(),
        url: z.string(),
        type: z.union([z.literal('tripadvisor'), z.literal('activity')]),
        image: z.string(),
      }),
    ),
    activity: z.array(
      z.object({
        lat: z.number(),
        lng: z.number(),
        name: z.string(),
        url: z.string(),
        type: z.union([z.literal('tripadvisor'), z.literal('activity')]),
        image: z.string(),
      }),
    ),
  }),
);

const configurationSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  range: z.number().optional(),
});

export type FilterParamsType = z.infer<typeof filterParamsSchema>;
export type CoordinatesParamsType = z.infer<typeof coordinatesSchema>;
export type StepsSelectionParamsType = z.infer<typeof stepsSelectionSchema>;
export type NeedsParamsType = z.infer<typeof needsParamsSchema>;
export type TravelModeType = z.infer<typeof travelModeSchema>;

export const coordinatesParser = parseAsJson(
  coordinatesSchema.parse,
).withDefault([{}, {}]);

export const registerDialogParser = parseAsBoolean.withDefault(false);
export const filterParamsParser = parseAsJson(filterParamsSchema.parse);
export const configurationParamsParser = parseAsJson(
  configurationSchema.parse,
).withDefault({
  startDate: addDays(new Date().toISOString(), 1).toISOString(),
  endDate: addDays(new Date().toISOString(), 2).toISOString(),
});
export const stepsSelectionSchemaParser = parseAsJson(
  stepsSelectionSchema.parse,
).withDefault([]);
export const travelModeParamsParser = parseAsJson(
  travelModeSchema.parse,
).withDefault({
  mode: 'tourist',
});
export const needsParamsParser = parseAsJson(
  needsParamsSchema.parse,
).withDefault({
  voyagePartner: 'family',
  physicalCondition: 'active',
  firstTime: true,
  bikeType: 'VAE',
  budget: {
    min: undefined,
    max: undefined,
  },
  numberOfPeople: 1,
});

export const tripIdParser = parseAsInteger.withDefault(-1);
export const searchParamsCache = createSearchParamsCache({
  coordinates: coordinatesParser,
  filters: filterParamsParser,
  needs: needsParamsParser,
  travelMode: travelModeParamsParser,
  stepsSelection: stepsSelectionSchemaParser,
  configuration: configurationParamsParser,
  tripId: tripIdParser,
  step: parseAsInteger.withDefault(1),
});
